import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
} from "reactstrap";
import { Icon } from "../../components/Component";
import { useNavigate, useLocation } from "react-router-dom";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import FormInput from "../form-components/InputComponent";
import FormTextArea from "../form-components/TextAreaComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormSelect from "../form-components/SelectComponent";
import { FormProvider, useForm } from "react-hook-form";
import { expensesApis } from "../../api/Expenses";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { Upload } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import { getDirtyFieldsOnly } from "../../utils/use-form/GetDirtyFieldsOnly";

const NewAdvance: React.FC = () => {
  const dispatch = useDispatch();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | number | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportOptions, setReportOptions] = useState<Array<{ value: string, label: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userOptions, setUserOptions] = useState<Array<{ value: string, label: string }>>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [advanceData, setAdvanceData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [selectedReportDateRange, setSelectedReportDateRange] = useState<{ startDate: string | null, endDate: string | null }>({ startDate: null, endDate: null });

  const methods = useForm({
    mode: "onChange",
  });

  const paymentMode = methods.watch("paymentMode");

  // Navigation: Update menu data with current location state
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  // State Management: Initialize component state from navigation location
  useEffect(() => {
    const {
      id: projectIdFromState,
      projectId: projectIdFromStateAlt,
      advanceData: advanceDataFromState,
      isEditMode: isEditModeFromState,
      isViewMode: isViewModeFromState,
      linkToReportMode: linkToReportModeFromState,
      createdBy: createdByFromState
    } = location.state || {};
    const finalProjectId = projectIdFromState || projectIdFromStateAlt;

    if (finalProjectId) {
      setProjectId(finalProjectId);
    }

    if (advanceDataFromState && (isEditModeFromState || isViewModeFromState || linkToReportModeFromState)) {
      setAdvanceData(advanceDataFromState);
      setIsEditMode(isEditModeFromState || false);
      setIsViewMode(isViewModeFromState || false);
    }

    if (createdByFromState) {
      setCreatedBy(createdByFromState);
    }
  }, [location.state]);

  // Data Loading: Load expense reports dropdown options
  useEffect(() => {
    const loadReports = async () => {
      if (!projectId) return;

      setLoadingReports(true);
      try {
        const response = await expensesApis.fetchReportDropdown(projectId);

        if (response && response.status) {
          const reportsList = response.data || [];

          const options = reportsList.map((report: any) => ({
            value: report.expenseReportId?.toString() || report.id?.toString() || '',
            label: report.reportName || report.name || 'Unnamed Report'
          }));

          setReportOptions(options);
        } else {
          setReportOptions([]);
        }
      } catch (error: any) {
        // Show exact API response error message - no frontend handling
        const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || error?.response || JSON.stringify(error) || "API Error";
        setReportOptions([]);

        Swal.fire({
          title: "Error Loading Reports",
          text: errorMessage,
          icon: "warning",
          confirmButtonText: "OK"
        });
      } finally {
        setLoadingReports(false);
      }
    };

    loadReports();
  }, [projectId]);

  // Data Loading: Load project users dropdown options
  useEffect(() => {
    const loadUsers = async () => {
      if (!projectId) return;

      setLoadingUsers(true);
      try {
        const response = await expensesApis.fetchProjectUsers(projectId);

        if (response && response.status) {
          const usersList = response.data || [];

          const options = usersList.map((user: any) => ({
            value: user.userId?.toString() || user.id?.toString() || '',
            label: user.userName || user.name || user.email || 'Unnamed User'
          }));

          setUserOptions(options);
        } else {
          setUserOptions([]);
        }
      } catch (error: any) {
        // Show exact API response error message - no frontend handling
        const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || error?.response || JSON.stringify(error) || "API Error";
        setUserOptions([]);

        Swal.fire({
          title: "Error Loading Users",
          text: errorMessage,
          icon: "warning",
          confirmButtonText: "OK"
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [projectId]);

  // Fetch report details to get date range for validation
  const fetchReportDetails = async (reportId: string | number) => {
    if (!reportId) {
      setSelectedReportDateRange({ startDate: null, endDate: null });
      return;
    }

    try {
      const response = await expensesApis.fetchReport(reportId, {
        status: "Pending"
      });
      if (response && response.status && response.data) {
        const reportData = response.data;
        setSelectedReportDateRange({
          startDate: reportData.startDate || null,
          endDate: reportData.endDate || null
        });
      } else {
        setSelectedReportDateRange({ startDate: null, endDate: null });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setSelectedReportDateRange({ startDate: null, endDate: null });
    }
  };

  // Form Initialization: Set report value from navigation state
  useEffect(() => {
    const { reportId, fromPaymentAdvances } = location.state || {};
    if (reportId && reportOptions.length > 0 && !fromPaymentAdvances) {
      const matchingReport = reportOptions.find(report => report.value === reportId.toString());
      if (matchingReport) {
        methods.setValue('report', reportId.toString());
      }
    }
  }, [location.state, reportOptions, methods]);

  const selectedReport = methods.watch('report');
  // Report Selection: Fetch report details when report is selected
  useEffect(() => {
    if (selectedReport && selectedReport !== '') {
      fetchReportDetails(selectedReport);
    } else {
      setSelectedReportDateRange({ startDate: null, endDate: null });
    }
  }, [selectedReport]);

  // Form Population: Populate form fields with existing advance data
  useEffect(() => {
    if (advanceData && (isEditMode || isViewMode || location.state?.linkToReportMode)) {
      // Only proceed if we have the necessary options loaded
      if (userOptions.length > 0 && reportOptions.length > 0) {
        const matchingUser = userOptions.find(user =>
          user.label === advanceData.employeeName ||
          user.label.toLowerCase().includes(advanceData.employeeName?.toLowerCase() || '')
        );

        if (location.state?.linkToReportMode) {
          methods.setValue('report', '');
        } else {
          methods.setValue('report', advanceData.expenseReportId?.toString() || '');
        }
        methods.setValue('paymentFrom', matchingUser?.value || '');

        if (currentUser?.genericRole?.toLowerCase() === "admin" && advanceData.paymentTo) {
          const matchingPaymentToUser = userOptions.find(user =>
            user.value === advanceData.paymentTo?.toString()
          );
          methods.setValue('paymentTo', matchingPaymentToUser?.value || '');
        }

        methods.setValue('amount', advanceData.amount?.toString() || '');
        methods.setValue('category', advanceData.paymentType || 'Advance');

        let parsedDate = dayjs();
        if (advanceData.requestDate) {
          if (dayjs(advanceData.requestDate, 'DD-MMM-YYYY', true).isValid()) {
            parsedDate = dayjs(advanceData.requestDate, 'DD-MMM-YYYY');
          } else if (dayjs(advanceData.requestDate, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
            parsedDate = dayjs(advanceData.requestDate, 'YYYY-MM-DD HH:mm:ss');
          } else if (dayjs(advanceData.requestDate).isValid()) {
            parsedDate = dayjs(advanceData.requestDate);
          }
        }
        methods.setValue('actionDate', parsedDate);
        methods.setValue('paymentMode', advanceData.paidThrough || 'Cash');
        const referenceValue = advanceData.referenceId || advanceData.reference || '';
        const descriptionValue = advanceData.description || advanceData.purpose || '';
        methods.setValue('referenceId', referenceValue);
        methods.setValue('description', descriptionValue);


        if (advanceData.paidThrough === 'Cheque') {
          methods.setValue('chequeNumber', advanceData.chequeNumber || '');
          if (advanceData.chequeDate) {
            let parsedChequeDate = dayjs();
            if (dayjs(advanceData.chequeDate, 'DD-MMM-YYYY', true).isValid()) {
              parsedChequeDate = dayjs(advanceData.chequeDate, 'DD-MMM-YYYY');
            } else if (dayjs(advanceData.chequeDate, 'YYYY-MM-DD', true).isValid()) {
              parsedChequeDate = dayjs(advanceData.chequeDate, 'YYYY-MM-DD');
            } else if (dayjs(advanceData.chequeDate).isValid()) {
              parsedChequeDate = dayjs(advanceData.chequeDate);
            }
            methods.setValue('chequeDate', parsedChequeDate);
          }
        }

        if (advanceData.dms && Array.isArray(advanceData.dms) && advanceData.dms.length > 0) {

          const existingFiles: File[] = [];

          advanceData.dms.forEach((dmsItem: any, index: number) => {
            const mockFile = new File([''], dmsItem.fileName || `file_${index}`, {
              type: 'application/octet-stream'
            });

            (mockFile as any).originalUrl = dmsItem.filePath;
            (mockFile as any).docId = dmsItem.docId;
            (mockFile as any).docHash = dmsItem.docHash;
            (mockFile as any).blobName = dmsItem.blobName;
            (mockFile as any).isExisting = true;


            existingFiles.push(mockFile);
          });

          setUploadedFiles(existingFiles);
        }
      }
    }
  }, [advanceData, isEditMode, isViewMode, location.state?.linkToReportMode, userOptions, reportOptions, currentUser?.genericRole, methods]);

  // File Management: Remove uploaded file with confirmation
  const removeFile = (index: number) => {
    const fileToRemove = uploadedFiles[index];
    const fileName = fileToRemove?.name || 'file';

    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete "${fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2557d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
      }
    });
  };

  // Form Submission: Handle form submission with validation and API calls
  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {

      if (!projectId) {
        Swal.fire({
          title: "Error!",
          text: "Project ID is required to create a payment advance",
          icon: "error",
          confirmButtonText: "OK"
        });
        setLoading(false);
        return;
      }

      let dirtyData: any = {};
      let hasFileChanges = false;

      if (isEditMode || location.state?.linkToReportMode) {
        const { dirtyFields } = methods.formState;
        dirtyData = getDirtyFieldsOnly(data, dirtyFields) || {};

        if (location.state?.linkToReportMode && data.report) {
          dirtyData.report = data.report;
        }

        const existingFiles = uploadedFiles.filter(file => (file as any).isExisting);
        const newFiles = uploadedFiles.filter(file => !(file as any).isExisting);
        const originalDmsFiles = advanceData?.dms || [];
        const originalFileCount = originalDmsFiles.length;
        const currentExistingFileCount = existingFiles.length;

        hasFileChanges = newFiles.length > 0 || currentExistingFileCount < originalFileCount;


        if (Object.keys(dirtyData).length === 0 && !hasFileChanges && !location.state?.linkToReportMode) {
          Swal.fire({
            title: "No Changes",
            text: "No changes were made to the payment advance",
            icon: "info",
            confirmButtonText: "OK"
          });
          setLoading(false);
          return;
        }
      }

      const reportValue = data.report;
      const isReportSelected = reportValue && (
        typeof reportValue === 'string' ? reportValue.trim() !== '' && reportValue !== '0' :
          typeof reportValue === 'object' ? reportValue.value && reportValue.value.toString().trim() !== '' && reportValue.value !== '0' :
            false
      );

      if (location.state?.linkToReportMode && !isReportSelected) {
        Swal.fire({
          title: "Error!",
          text: "Please select a report to link this advance",
          icon: "error",
          confirmButtonText: "OK"
        });
        setLoading(false);
        return;
      }

      if (location.state?.linkToReportMode && isReportSelected) {

        const selectedReportExists = reportOptions.some(option => {
          if (typeof reportValue === 'string') {
            return option.value === reportValue;
          } else if (typeof reportValue === 'object' && reportValue.value) {
            return option.value === reportValue.value.toString();
          }
          return false;
        });

        if (!selectedReportExists) {
          Swal.fire({
            title: "Error!",
            text: "The selected report is no longer available. Please refresh and select a different report.",
            icon: "error",
            confirmButtonText: "OK"
          });
          setLoading(false);
          return;
        }
      }

      const formDataToSend = new FormData();

      // Prepare the expense data
      // Extract report ID from the selected value (handle both string and object)
      let reportId = null;
      if (data.report) {
        if (typeof data.report === 'string') {
          reportId = parseInt(data.report);
        } else if (typeof data.report === 'object' && data.report.value) {
          reportId = parseInt(data.report.value);
        }
      }

      // Extract payment from ID from the selected value (handle both string and object)
      let paymentFromId = null;
      if (data.paymentFrom) {
        if (typeof data.paymentFrom === 'string') {
          paymentFromId = parseInt(data.paymentFrom);
        } else if (typeof data.paymentFrom === 'object' && data.paymentFrom.value) {
          paymentFromId = parseInt(data.paymentFrom.value);
        }
      }

      // Extract payment to ID from the selected value (handle both string and object) - only for admin users
      let paymentToId = null;
      if (data.paymentTo && currentUser?.genericRole?.toLowerCase() === "admin") {
        if (typeof data.paymentTo === 'string') {
          paymentToId = parseInt(data.paymentTo);
        } else if (typeof data.paymentTo === 'object' && data.paymentTo.value) {
          paymentToId = parseInt(data.paymentTo.value);
        }
      }

      // Create base expense data
      let expenseData: any = {
        expenseReportId: reportId,
        projectId: parseInt(projectId.toString()),
        paymentFrom: paymentFromId,
        ...(paymentToId && { paymentTo: paymentToId }), // Only include paymentTo if it's set
        category: data.category || 'Advance',
        actionDate: data.actionDate ? dayjs(data.actionDate).format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        amount: parseFloat(data.amount),
        description: data.description || 'Payment Advance',
        paymentMode: data.paymentMode || 'Cash',
        currencyId: data.currencyId || 1,
        referenceId: data.referenceId || `REF-${Date.now()}`
      };

      // For edit mode or link to report mode, only include dirty fields in the payload, but always include projectId and expenseId
      if (isEditMode || location.state?.linkToReportMode) {
        const filteredExpenseData: any = {
          projectId: parseInt(projectId.toString())
        };

        // Always include expenseId for validation
        filteredExpenseData.expenseId = advanceData?.expenseId;

        // For link to report mode, also include expenseReportId from the selected report
        if (location.state?.linkToReportMode) {
          filteredExpenseData.expenseReportId = reportId;
        }

        // Only include fields that were actually changed
        Object.keys(dirtyData).forEach(key => {
          if (expenseData.hasOwnProperty(key)) {
            filteredExpenseData[key] = expenseData[key];
          }
        });

        // If there are file changes but no form field changes, we still need to send the request
        // So we keep the filtered data same
        expenseData = filteredExpenseData;
      }

      // Add cheque fields if payment mode is Cheque (only if dirty in edit mode or link to report mode)
      if (data.paymentMode === 'Cheque') {
        if (!isEditMode || !location.state?.linkToReportMode || dirtyData.paymentMode || dirtyData.chequeNumber || dirtyData.chequeDate) {
          expenseData.chequeNumber = data.chequeNumber;
          expenseData.chequeDate = data.chequeDate ? dayjs(data.chequeDate).format('YYYY-MM-DD') : null;
        }
      }

      // Handle file deletion and updates for edit mode or link to report mode
      if (isEditMode || location.state?.linkToReportMode) {
        const existingFiles = uploadedFiles.filter(file => (file as any).isExisting);
        const newFiles = uploadedFiles.filter(file => !(file as any).isExisting);

        // Check if files were actually changed
        const originalDmsFiles = advanceData?.dms || [];
        const originalFileCount = originalDmsFiles.length;
        const currentExistingFileCount = existingFiles.length;
        const hasNewFiles = newFiles.length > 0;
        const hasDeletedFiles = currentExistingFileCount < originalFileCount;

        // Process file changes if there are actual changes
        if (hasNewFiles || hasDeletedFiles) {
          // Update fileData format for edit mode - backend expects object with docKeys and deletedDocs
          const fileData: any = {};

          // Add new files to docKeys
          if (hasNewFiles) {
            fileData.docKeys = newFiles.map((file, index) => ({
              order: index,
              module: "expenses",
              moduleFileType: "payment_in"
            }));
          }

          // Add deletedDocs for files that were removed
          if (hasDeletedFiles) {
            const currentDocIds = existingFiles.map(file => (file as any).docId).filter(Boolean);
            const deletedDocIds = originalDmsFiles
              .filter((dmsFile: any) => !currentDocIds.includes(dmsFile.docId))
              .map((dmsFile: any) => dmsFile.docId);

            if (deletedDocIds.length > 0) {
              fileData.deletedDocs = deletedDocIds.map((docId: number) => ({ docId: docId }));
            }
          }

          // Only add fileData to expenseData if it has content
          if (Object.keys(fileData).length > 0) {
            expenseData.fileData = fileData;
          }
        }
      } else {
        // For create mode, include fileData as array (backend expects array, not object)
        if (uploadedFiles.length > 0) {
          expenseData.fileData = uploadedFiles.map((file, index) => ({
            order: index,
            module: "expenses",
            moduleFileType: "payment_in"
          }));
        }
      }

      // Determine which files to upload based on mode
      let filesToUpload: File[] = [];
      
      if (isEditMode || location.state?.linkToReportMode) {
        // In edit mode, only upload new files (not existing ones)
        filesToUpload = uploadedFiles.filter(file => !(file as any).isExisting);
      } else {
        // In create mode, upload all files
        filesToUpload = uploadedFiles;
      }

      // Add files to FormData if any need to be uploaded
      for (const file of filesToUpload) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          Swal.fire({
            icon: "error",
            title: "File Size Error",
            text: `File "${file.name}" is too large. Maximum size is 10MB.`,
            confirmButtonText: "Ok",
          });
          setLoading(false);
          return;
        }

        // Add file to FormData
        formDataToSend.append("files", file, file.name);
      }
      // Send expense data as JSON string wrapped in data object
      formDataToSend.append("data", JSON.stringify(expenseData));

      // Use appropriate API based on mode
      let response;
      if ((isEditMode && advanceData?.expenseId) || location.state?.linkToReportMode) {
        response = await expensesApis.updatePaymentIn(formDataToSend);
      } else {
        response = await expensesApis.createPaymentIn(formDataToSend);
      }

      if (response && response.status) {
        Swal.fire({
          title: "Success!",
          text: (isEditMode || location.state?.linkToReportMode) ? "Payment advance updated successfully!" : "Payment advance created successfully!",
          icon: "success",
          confirmButtonText: "OK"
        });

        // Navigate to report breakdown page after success (never null in URL)
        const reportsPath = projectId != null && !isNaN(Number(projectId)) ? `/pms/expenses/reports/${projectId}` : '/pms/expenses/reports';
        if (location.state?.fromReportBreakDown && location.state?.reportId) {
          navigate(reportsPath, {
            state: {
              projectId: projectId,
              reportId: location.state.reportId,
              reportName: location.state.reportName,
              openReportBreakDown: true,
              refreshReportBreakDown: true
            }
          });
        } else if (data.report) {
          // Navigate to report breakdown for the selected report
          navigate(reportsPath, {
            state: {
              projectId: projectId,
              reportId: data.report,
              reportName: reportOptions.find(option => option.value === data.report)?.label || 'Report',
              openReportBreakDown: true,
              refreshReportBreakDown: true
            }
          });
        } else {
          // Otherwise go back to payment advances
          navigate(-1);
        }
      } else {
        // Show exact API response error message - no frontend handling
        const errorMessage = response?.message || response?.data?.message || response?.data || JSON.stringify(response) || "API Error";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // Show exact API response error message - no frontend handling
      const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || error?.response || JSON.stringify(error) || "API Error";

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Navigation: Handle back button navigation based on source
  const handleBack = () => {
    // Navigate back based on where we came from (never null in URL)
    const reportsPath = projectId != null && !isNaN(Number(projectId)) ? `/pms/expenses/reports/${projectId}` : '/pms/expenses/reports';
    if (location.state?.fromReportBreakDown) {
      // If we came from ReportBreakDown, go back to reports
      navigate(reportsPath, {
        state: {
          projectId: projectId,
          reportId: location.state.reportId,
          reportName: location.state.reportName,
          openReportBreakDown: true, // Flag to automatically open the report breakdown modal
          refreshReportBreakDown: true // Flag to refresh the data in the modal
        }
      });
    } else if (location.state?.fromPaymentAdvances && location.state?.backUrl) {
      // If we came from PaymentAdvances, go back to payment advances
      navigate(location.state.backUrl, {
        state: {
          projectId: projectId
        }
      });
    } else {
      // Otherwise go back to payment advances
      navigate(-1);
    }
  };

  return (
    <>
      <Head title={
        isEditMode ? "Edit Payment Advance" :
          location.state?.linkToReportMode ? "Link Payment Advance to Report" :
            "New Payment Advance"
      } />
      <Content>
        <Card className="card-bordered">
          {/* Full card view without aside menu */}
          <div className="card-inner" style={{ minHeight: "87vh" }}>
            <FormProvider {...methods}>
              <div>
                <div
                  className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                  style={{ minHeight: "20px", border: "0px" }}
                >
                  <div>
                    <h5 style={{ marginBottom: "5px" }}>
                      {isEditMode ? "Edit Payment Advance" :
                        location.state?.linkToReportMode ? "Link Payment Advance to Report" :
                          "New Payment Advance"}
                    </h5>
                    {createdBy && (isEditMode || isViewMode) && (
                      <small className="text-muted" style={{ fontSize: "12px", fontWeight: "500" }}>
                        Created by: {createdBy}
                      </small>
                    )}
                  </div>
                  <div className="toggle-wrap nk-block-tools-toggle">
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                      <li className="pt-0 pb-0">
                        <Button
                          type="button"
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={handleBack}
                          style={{ borderRadius: "6px", height: "32px", fontSize: '12px', padding: '4px 12px', fontWeight: "500", fontFamily: 'Inter, Roboto, sans-serif' }}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Main Content */}
                <div>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <div>
                      {/* Report */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <FormSelect
                            name="report"
                            label={location.state?.linkToReportMode ? "Expense Header" : "Expense Header"}
                            required={location.state?.linkToReportMode || false}
                            placeholder="Select Expense Header"
                            options={reportOptions}
                            disabled={
                              loadingReports ||
                              isViewMode ||
                              isEditMode ||
                              (location.state?.fromReportBreakDown && location.state?.reportId)
                            }
                          />
                        </div>
                   
                        <div className="col-md-6 mb-3">
                          <FormSelect
                            name="paymentFrom"
                            label="Payment From"
                            placeholder="Select Employee"
                            options={userOptions}
                            required={true}
                            disabled={loadingUsers || isViewMode}
                          />
                        </div>
                        {/* Payment To field - only for admin users */}
                        {currentUser?.genericRole?.toLowerCase() === "admin" && (
                          <div className="col-md-6 mb-3">
                            <FormSelect
                              name="paymentTo"
                              label="Payment To"
                              placeholder="Select Employee"
                              options={userOptions}
                              required={true}
                              disabled={loadingUsers || isViewMode}
                            />
                          </div>
                        )}
                      </div>

                      {/* Amount and Category */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <FormInput
                            name="amount"
                            label="Amount (INR)"
                            placeholder="Enter amount"
                            type="number"
                            min="0"
                            required={true}
                            disabled={isViewMode}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <FormInput
                            name="category"
                            label="Category"
                            placeholder="Enter category"
                            type="text"
                            required={true}
                            defaultValue="Advance"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      {/* Date and Payment Mode */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <FormDatePicker
                            name="actionDate"
                            label="Date"
                            required={true}
                            defaultValue={dayjs()}
                            minDate={selectedReportDateRange.startDate ? new Date(selectedReportDateRange.startDate) : undefined}
                            maxDate={new Date()}
                            disabled={isViewMode}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <FormSelect
                            name="paymentMode"
                            label="Paid Through"
                            options={[
                              { value: 'Cash', label: 'Cash' },
                              { value: 'Bank Transfer', label: 'Bank Transfer' },
                              { value: 'UPI', label: 'UPI' },
                              { value: 'Cheque', label: 'Cheque' },
                              { value: 'Credit', label: 'Credit' }
                            ]}
                            placeholder="Select payment method"
                            required={true}
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      {/* Cheque fields - shown only when Cheque is selected */}
                      {paymentMode === 'Cheque' && (
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <FormInput
                              name="chequeNumber"
                              label="Cheque Number *"
                              placeholder="Enter cheque number"
                              type="text"
                              required={true}
                              disabled={isViewMode}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <FormDatePicker
                              name="chequeDate"
                              label="Cheque Date *"
                              required={true}
                              maxDate={new Date()}
                              disabled={isViewMode}
                            />
                          </div>
                        </div>
                      )}

                      {/* Reference ID and Description */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <FormTextArea
                            name="description"
                            label="Description"
                            placeholder="Enter Description"
                            maxLength={500}
                            required={true}
                            rows={3}
                            disabled={isViewMode}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <FormInput
                            name="referenceId"
                            label="Reference #"
                            placeholder="Enter reference number"
                            type="text"
                            required={true}
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      {/* File Upload and Uploaded Files List */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div style={{ marginBottom: '8px' }}>
                            <label style={{
                              fontWeight: 500,
                              color: "#262626",
                              fontSize: '14px',
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <Icon name="upload" style={{ fontSize: '16px', marginRight: '8px' }} />
                              File Upload (Multiple)
                            </label>
                          </div>
                          <Upload
                            beforeUpload={() => false}
                            multiple={true}
                            maxCount={5}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                            listType="text"
                            showUploadList={{
                              showPreviewIcon: false,
                              showRemoveIcon: false,
                              showDownloadIcon: false,
                            }}
                            style={{ width: "100%", maxWidth: "100%" }}
                            disabled={isViewMode}
                            onChange={(info: any) => {
                              const { fileList } = info;
                              const newFiles = fileList.map((fileItem: any) => {
                                if (fileItem.originFileObj) {
                                  // Mark new files as not existing
                                  (fileItem.originFileObj as any).isExisting = false;
                                  return fileItem.originFileObj;
                                }
                                return null;
                              }).filter(Boolean);

                              setUploadedFiles(prev => {
                                const existingFiles = prev.filter(file => (file as any).isExisting);
                                return [...existingFiles, ...newFiles];
                              });
                            }}
                            onRemove={(file: any) => {
                              // Handle file removal
                              const fileToRemove = file.originFileObj;
                              if (fileToRemove) {
                                setUploadedFiles(prev => prev.filter(f => f !== fileToRemove));
                              }
                              return true;
                            }}
                          >
                            <Button
                              type="button"
                              icon={<UploadOutlined />}
                              size="small"
                              style={{
                                height: "28px",
                                fontWeight: 400,
                                backgroundColor: "#ffffff",
                                borderColor: "#d9d9d9",
                                color: "#595959",
                                width: "100%",
                                fontSize: "12px"
                              }}
                            >
                              Choose Files
                            </Button>
                          </Upload>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            {/* Uploaded Files List */}
                            {uploadedFiles.length > 0 && (
                              <div style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                padding: '8px',
                                border: '1px solid #e9ecef',
                                height: '100%'
                              }}>
                                <div style={{
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  color: '#495057',
                                  marginBottom: '8px'
                                }}>
                                  Uploaded Files ({uploadedFiles.length})
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                  {uploadedFiles.map((file, index) => (
                                    <div key={index} style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '6px 8px',
                                      backgroundColor: '#fff',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '3px',
                                      marginBottom: '4px'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Icon
                                          name={file.type.startsWith('image/') ? 'image' : 'file-text'}
                                          className="text-primary me-2"
                                          style={{ fontSize: '14px' }}
                                        />
                                        <span style={{
                                          fontSize: '12px',
                                          color: '#495057'
                                        }}>
                                          {file.name}
                                        </span>
                                        <span style={{
                                          fontSize: '11px',
                                          color: '#6c757d',
                                          marginLeft: '8px'
                                        }}>
                                          ({(file as any).isExisting ? 'Existing' : `${(file.size / 1024 / 1024).toFixed(2)} MB`})
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeFile(index);
                                        }}
                                        style={{
                                          padding: '2px 6px',
                                          fontSize: '11px',
                                          borderRadius: '3px'
                                        }}
                                      >
                                        <Icon name="trash" style={{ fontSize: '11px' }} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Submit Buttons - Hide in view mode */}
                      {!isViewMode && (
                        <Row className="mt-4">
                          <Col lg="12">
                            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                              <li>
                                <Button
                                  className="btn btn-primary btn-md"
                                  type="submit"
                                  color="primary"
                                  disabled={loading}
                                  style={{ height: '32px', fontSize: '14px', padding: '4px 12px', fontFamily: 'Inter, Roboto, sans-serif' }}
                                >
                                  <span>{isEditMode ? "Update" : "Submit"}</span>
                                </Button>
                              </li>
                              <li>
                                <a
                                  href="#cancel"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    handleBack();
                                  }}
                                  className="link link-light"
                                  style={{ pointerEvents: loading ? 'none' : 'auto' }}
                                >
                                  Cancel
                                </a>
                              </li>
                            </ul>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </FormProvider>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default NewAdvance;
